import * as THREE from 'three';
import { type GraphicsQuality } from '../core/settings';

// ---------------------------------------------------------------------------
// Water Puddle System — decal-only (SSR provides reflections)
//   medium/high/ultra: MeshStandardMaterial decal (roughness 0.05, SSR-reflective)
//   low:               disabled
//
// Previous: high/ultra used THREE.Reflector (real-time mirror pass, ~3ms per puddle).
// Now SSR handles reflections for all low-roughness surfaces automatically,
// so Reflector is no longer needed. Saves ~3ms from removed extra scene renders.
// ---------------------------------------------------------------------------

// Default puddle placements per map (shaded / sheltered areas)
const PUDDLE_PLACEMENTS: Record<string, Array<{ x: number; y: number; z: number; radius: number }>> = {
  de_outpost_v6: [
    { x: -32, y: 0, z: 20, radius: 1.8 },    // Long A near guard shack
    { x: 4, y: 0, z: 5, radius: 2.0 },        // Mid courtyard near tower base
    { x: -50, y: 1.0, z: -35, radius: 1.4 },  // A site on platform
    { x: 38, y: 0, z: -28, radius: 1.5 },     // B site near containers
    { x: 0, y: 0, z: -62, radius: 1.3 },      // CT spawn courtyard
    { x: 34, y: 0, z: 14, radius: 1.6 },      // B tunnels bend area
    { x: -22, y: 2.8, z: -23, radius: 1.0 },  // Catwalk deck
  ],
};

interface PuddleEntry {
  mesh: THREE.Mesh;
  position: THREE.Vector3;
  radius: number;
}

export class PuddleSystem {
  private scene: THREE.Scene;
  private quality: GraphicsQuality;
  private puddles: PuddleEntry[] = [];

  constructor(scene: THREE.Scene, quality: GraphicsQuality) {
    this.scene = scene;
    this.quality = quality;
  }

  /** Add a single puddle at the given position. */
  addPuddle(position: THREE.Vector3, radius: number): void {
    if (this.quality === 'low') return;
    const entry = this.createPuddle(position, radius);
    if (entry) {
      this.scene.add(entry.mesh);
      this.puddles.push(entry);
    }
  }

  /** Load default puddle placements for the given map ID. */
  loadMapPuddles(mapId: string): void {
    const placements = PUDDLE_PLACEMENTS[mapId];
    if (!placements) return;
    for (const p of placements) {
      this.addPuddle(new THREE.Vector3(p.x, p.y, p.z), p.radius);
    }
  }

  /** Per-frame update — no-op now that Reflector is removed. Kept for API compat. */
  update(_camera: THREE.Camera): void {
    // SSR handles reflections; no per-frame Reflector visibility management needed.
  }

  /** Switch quality tier — recreates all puddles. */
  setQuality(quality: GraphicsQuality): void {
    if (quality === this.quality) return;
    // Store current positions/radii
    const saved = this.puddles.map(p => ({ position: p.position.clone(), radius: p.radius }));
    this.disposeAll();
    this.quality = quality;
    for (const s of saved) {
      this.addPuddle(s.position, s.radius);
    }
  }

  dispose(): void {
    this.disposeAll();
  }

  // ---------------------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------------------

  private createPuddle(position: THREE.Vector3, radius: number): PuddleEntry | null {
    if (this.quality === 'low') return null;

    // All quality tiers use decal puddles. SSR provides reflections for
    // low-roughness surfaces automatically. Higher quality tiers get more
    // geometry segments for smoother circles.
    const segments = this.quality === 'ultra' ? 32 : this.quality === 'high' ? 28 : 24;
    const geo = new THREE.CircleGeometry(radius, segments);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x445566,
      roughness: 0.05,  // Very smooth — SSR picks this up for reflections
      metalness: 0.1,
      transparent: true,
      opacity: 0.6,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.copy(position);
    mesh.position.y += 0.001;
    mesh.renderOrder = 50;

    return {
      mesh,
      position: mesh.position,
      radius,
    };
  }

  private disposeAll(): void {
    for (const entry of this.puddles) {
      this.scene.remove(entry.mesh);
      entry.mesh.geometry.dispose();
      if (entry.mesh.material instanceof THREE.Material) {
        entry.mesh.material.dispose();
      }
    }
    this.puddles = [];
  }
}
